import LocationOnIcon from '@mui/icons-material/LocationOn';
import Skeleton from '@mui/material/Skeleton';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as IntersectIcon } from '../../assets/Intersect.svg';
import { PATHS } from '../../config/consts';
import { SITE_CATEGORY } from '../../config/sites';
import { useDataGrid } from '../../helpers/dataGrid';
import useBreakpoints from '../../helpers/useBreakpoints';
import { isFetchingMessages } from '../../modules/localization/selectors';
import { getSitesList } from '../../modules/sites/actions';
import BillingType from '../BillingType';
import DataGrid from '../DataGrid';
import NameColumn from '../DataGrid/NameColumn';
import EllipsisText from '../EllipsisText';
import SiteStatus from '../SiteStatus';
import SiteType from '../SiteType';
import messages from './messages';
import { NoRowsOverlay } from './NoRowsOverlay';
import { NoRowsSearchOverlay } from './NoRowsSearchOverlay';
import { SitesDataGridRowMenu } from './SitesDataGridRowMenu';

const SitesDataGrid = ({
  companyId,
  context = '',
  data,
  didInvalid = false,
  filter,
  isFetching,
  onAddSite,
  onChangeFilter,
  order,
  page,
  resultsFiltered,
  rowsPerPage,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { downSm, upLg, upMd, upSm } = useBreakpoints();
  const isLocalizationLoaded = useSelector(isFetchingMessages);

  const { onOrderBy, onRowsPerPageChange, onSelectPage } = useDataGrid(
    (...params) => dispatch(getSitesList(...params)),
    {
      companyId,
      context,
      filter,
      order,
      page,
      rowsPerPage,
    },
    didInvalid,
    isFetching
  );

  const columns = useMemo(
    () => [
      {
        disableColumnMenu: true,
        field: 'name',
        flex: 1,
        headerName: formatMessage(messages.sitesDataGridColumnsName),
        renderCell: (site) =>
          isFetching ? (
            <Skeleton animation="wave" height={25} width="80%" />
          ) : (
            <NameColumn
              iconComponent={
                site.value?.siteCategory === SITE_CATEGORY.ACCESS_COMMANDER_REMOTE ? (
                  <IntersectIcon height={24} width={24} />
                ) : (
                  <LocationOnIcon />
                )
              }
              name={site.value?.name}
            />
          ),
        sortable: true,
        width: 200,
      },
      {
        disableColumnMenu: true,
        field: 'my2nId',
        flex: 1,
        headerName: formatMessage(messages.sitesDataGridColumnsMy2nId),
        renderCell: (site) =>
          isFetching ? (
            <Skeleton animation="wave" height={25} width="80%" />
          ) : (
            <EllipsisText>{site.value?.my2nId}</EllipsisText>
          ),
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'type',
        flex: 1,
        headerName: formatMessage(messages.sitesDataGridColumnsType),
        renderCell: (site) =>
          isFetching ? (
            <Skeleton animation="wave" height={25} width="80%" />
          ) : (
            <SiteType category={site.value?.siteCategory} type={site.value?.type} />
          ),
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'model',
        flex: 1,
        headerName: formatMessage(messages.sitesDataGridColumnsPaymentModel),
        renderCell: (site) =>
          isFetching ? (
            <Skeleton animation="wave" height={25} width="80%" />
          ) : (
            <BillingType
              billingType={site.value?.mobileVideo?.licenceModel?.billingType}
              siteCategory={site.value?.siteCategory}
            />
          ),
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'status',
        flex: 1,
        headerName: formatMessage(
          downSm
            ? messages.sitesDataGridColumnsMobileVideoStatusMobile
            : messages.sitesDataGridColumnsMobileVideoStatusDesktop
        ),
        renderCell: (site) =>
          isFetching ? (
            <Skeleton animation="wave" height={25} width="80%" />
          ) : (
            <SiteStatus licenceModel={site.value?.mobileVideo?.licenceModel} siteCategory={site.value?.siteCategory} />
          ),
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'rowMenu',
        headerName: '',
        renderCell: (site) =>
          isFetching ? (
            <Skeleton animation="wave" height={25} width="80%" />
          ) : (
            <SitesDataGridRowMenu site={site.value} />
          ),
        sortable: false,
        width: 65,
      },
    ],
    [downSm, isFetching, upLg, upMd, upSm, isLocalizationLoaded, formatMessage]
  );

  const rows = useMemo(
    () =>
      data.map((site) => ({
        id: site.id,
        isClickable: true,
        model: site,
        my2nId: site,
        name: site,
        rowMenu: site,
        status: site,
        type: site,
      })),
    [data]
  );

  const onClearSearch = () => {
    onChangeFilter({
      target: {
        name: 'fulltext',
        value: '',
      },
    });
  };

  return (
    <DataGrid
      columns={columns}
      columnVisibilityModel={{
        model: upSm,
        my2nId: upMd,
        type: upLg,
      }}
      dataTestId="sites-table"
      disableRowSelectionOnClick
      hideFooterPagination={isFetching}
      loading={isFetching}
      noRowsOverlay={
        filter.fulltext !== '' ? (
          <NoRowsSearchOverlay onClearSearch={onClearSearch} searchText={filter.fulltext} />
        ) : (
          <NoRowsOverlay onCreateSite={onAddSite} />
        )
      }
      onCellClick={(site) => {
        if (site.field !== 'rowMenu') {
          if (site?.value?.id && companyId) {
            navigate(PATHS.SITE_DASHBOARD.replace(':companyId', companyId).replace(':siteId', site.value?.id));
          }
        }
      }}
      onOrderBy={onOrderBy}
      onRowsPerPageChange={onRowsPerPageChange}
      onSelectPage={onSelectPage}
      order={order}
      page={page}
      resultsFiltered={resultsFiltered}
      resultsTotal={resultsFiltered}
      rows={rows}
      rowsPerPage={rowsPerPage}
    />
  );
};

SitesDataGrid.propTypes = {
  companyId: PropTypes.number.isRequired,
  context: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  didInvalid: PropTypes.bool,
  filter: PropTypes.shape({ fulltext: PropTypes.string }).isRequired,
  isFetching: PropTypes.bool.isRequired,
  onAddSite: PropTypes.func.isRequired,
  onChangeFilter: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  resultsFiltered: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default SitesDataGrid;
